'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  canProcessAvifInBrowser,
  convertJpgToAvif,
  convertPngToAvif,
  convertWebpToAvif,
  fileIsJpeg,
  fileIsPng,
  fileIsWebp,
  formatFileSize,
} from '@/lib/browser-avif-tools';
import { getBaseName, loadRasterImage } from '@/lib/browser-image-tools';
import { readWebpSource } from '@/lib/browser-webp-tools';

type Variant = 'jpg-to-avif' | 'png-to-avif' | 'webp-to-avif';

type SourceState = {
  file: File;
  previewUrl: string;
  width: number;
  height: number;
  size: number;
  transparencyLabel: string;
};

const copy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    inputLabel: string;
    accept: string;
    qualityLabel: string;
    actionLabel: string;
  }
> = {
  'jpg-to-avif': {
    title: 'Convert JPG to AVIF for smaller modern web assets',
    description:
      'Upload one JPG or JPEG, tune the AVIF quality, and export a smaller still image for modern web delivery.',
    badge: 'AVIF',
    inputLabel: 'Choose one JPG or JPEG or drop it here',
    accept: 'image/jpeg,.jpg,.jpeg',
    qualityLabel: 'AVIF quality',
    actionLabel: 'Convert to AVIF',
  },
  'png-to-avif': {
    title: 'Convert PNG to AVIF and keep alpha when possible',
    description:
      'Upload one PNG, choose the AVIF quality, and export a lighter still image while preserving transparency when the runtime supports it.',
    badge: 'AVIF',
    inputLabel: 'Choose one PNG or drop it here',
    accept: 'image/png,.png',
    qualityLabel: 'AVIF quality',
    actionLabel: 'Convert to AVIF',
  },
  'webp-to-avif': {
    title: 'Convert still WebP to AVIF',
    description:
      'Upload one still WebP, keep alpha where possible, and export a browser-first AVIF without switching to a server flow.',
    badge: 'AVIF',
    inputLabel: 'Choose one still WebP or drop it here',
    accept: 'image/webp,.webp',
    qualityLabel: 'AVIF quality',
    actionLabel: 'Convert to AVIF',
  },
};

export default function BrowserSourceToAvifConverter({ variant }: { variant: Variant }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<SourceState | null>(null);
  const [quality, setQuality] = useState(68);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [resultPreviewUrl, setResultPreviewUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const toolCopy = copy[variant];
  const isSupported = canProcessAvifInBrowser();

  useEffect(() => {
    return () => {
      [source?.previewUrl, downloadUrl, resultPreviewUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [downloadUrl, resultPreviewUrl, source]);

  function resetOutputState() {
    [downloadUrl, resultPreviewUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setDownloadUrl(null);
    setResultPreviewUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  function clearSource() {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  }

  async function buildSource(file: File): Promise<SourceState> {
    if (variant === 'jpg-to-avif') {
      if (!fileIsJpeg(file)) {
        throw new Error('Choose one JPG or JPEG file for this tool.');
      }

      const loaded = await loadRasterImage(file);
      return {
        file,
        previewUrl: URL.createObjectURL(file),
        width: loaded.width,
        height: loaded.height,
        size: file.size,
        transparencyLabel: 'JPG source is fully opaque',
      };
    }

    if (variant === 'png-to-avif') {
      if (!fileIsPng(file)) {
        throw new Error('Choose one PNG file for this tool.');
      }

      const loaded = await loadRasterImage(file);
      return {
        file,
        previewUrl: URL.createObjectURL(file),
        width: loaded.width,
        height: loaded.height,
        size: file.size,
        transparencyLabel: 'Transparency is preserved when present',
      };
    }

    if (!fileIsWebp(file)) {
      throw new Error('Choose one still WebP file for this tool.');
    }

    const webpSource = await readWebpSource(file);

    if (webpSource.metadata.animated) {
      throw new Error(
        'This WebP is animated. Use Animated WebP Maker or WebP Optimizer for animated WebP files.',
      );
    }

    return {
      file,
      previewUrl: URL.createObjectURL(file),
      width: webpSource.metadata.width,
      height: webpSource.metadata.height,
      size: file.size,
      transparencyLabel: webpSource.metadata.hasAlpha
        ? 'Still WebP transparency can be preserved'
        : 'No meaningful alpha detected',
    };
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      const nextSource = await buildSource(file);
      setSource((current) => {
        if (current?.previewUrl) {
          URL.revokeObjectURL(current.previewUrl);
        }

        return nextSource;
      });
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The source image could not be loaded.',
      );
    }
  }

  async function runConversion() {
    if (!source) {
      setErrorMessage('Choose one image first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another AVIF export right now.',
      );
      const result =
        variant === 'jpg-to-avif'
          ? await convertJpgToAvif({ file: source.file, quality })
          : variant === 'png-to-avif'
            ? await convertPngToAvif({ file: source.file, quality })
            : await convertWebpToAvif({ file: source.file, quality });
      const nextDownloadUrl = URL.createObjectURL(result.blob);
      const nextPreviewUrl = URL.createObjectURL(result.previewBlob);

      resetOutputState();
      setDownloadUrl(nextDownloadUrl);
      setResultPreviewUrl(nextPreviewUrl);
      setDownloadName(`${getBaseName(source.file.name)}.avif`);
      setResultSize(result.blob.size);
      setCreditMessage(formatToolCreditSuccess('AVIF conversion', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be created.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const savings =
    source && resultSize !== null
      ? `${Math.round((1 - resultSize / Math.max(source.size, 1)) * 100)}%`
      : null;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{toolCopy.title}</h2>
          <p>{toolCopy.description}</p>
        </div>
        <span className="tool-live-badge">{toolCopy.badge}</span>
      </div>

      {!isSupported ? (
        <p className="support-feedback support-feedback-error">
          This browser cannot encode AVIF locally yet. Use a modern Chromium or Safari build with
          ImageDecoder and OffscreenCanvas support.
        </p>
      ) : null}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            disabled={!isSupported}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : toolCopy.inputLabel}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.size)} • ${source.width} × ${source.height}`
                  : 'Still-image AVIF export only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={toolCopy.accept}
            className="tool-live-file-input"
            disabled={!isSupported}
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>{toolCopy.qualityLabel}</span>
              <input
                max="100"
                min="1"
                onChange={(event) => {
                  setQuality(Math.max(1, Number(event.target.value) || 68));
                  resetOutputState();
                }}
                step="1"
                type="range"
                value={quality}
              />
              <small>{quality}</small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing || !isSupported}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? 'Converting…' : toolCopy.actionLabel}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download AVIF
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>AVIF export summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Quality</strong>
              <span>{quality}</span>
            </div>
            <div>
              <strong>Source</strong>
              <span>{source ? source.transparencyLabel : 'No image loaded'}</span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            {savings ? (
              <div>
                <strong>Size delta</strong>
                <span>{savings}</span>
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Source image</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Source image preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Source preview</strong>
                  <span>Load one source image to prepare an AVIF export.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>AVIF preview</strong>
            <div className="tool-live-preview-frame">
              {resultPreviewUrl ? (
                <img alt="AVIF export preview" src={resultPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>No export yet</strong>
                  <span>Run the conversion to preview the AVIF result.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
